import React, { useState, useMemo, useEffect } from "react";
import { Popover, PopoverSurface, PopoverTrigger } from "@fluentui/react-components";
import { X, Plus, Calendar as CalendarIcon, ChevronDown, ChevronRight, Search, Diamond, AlertCircle, CheckCircle } from "lucide-react";
import Input from "../../../components/controls/input/customInput/CustomInput";
import { CalendarIcon as CalIcon } from "../../../modules/default-svg";
import Modal from "../../../components/controls/modal/Modal";
import styles from "./KeyEventsPopover.module.scss";
import { KEY_EVENT_TYPES, KEY_EVENT_ICON_TYPES, KEY_EVENT_REF_TYPES } from "../constants";
import { buildKeyEventTree, findNodeInTree, findAncestorIds } from "../utils/buildKeyEventTree";

const KEY_EVENT_ICONS = {
  [KEY_EVENT_ICON_TYPES.MILESTONE]: { icon: Diamond, label: "Milestone", color: "#9333EA" },
  [KEY_EVENT_ICON_TYPES.DEADLINE]: { icon: AlertCircle, label: "Deadline", color: "#DC2626" },
  [KEY_EVENT_ICON_TYPES.REVIEW]: { icon: CheckCircle, label: "Review", color: "#059669" },
};

// Scope/Task have no dedicated Roadmap/Timeline page of their own yet, so IsShared - whether an
// event also shows on the linked entity's own page - has no visible effect for these link types.
const isScopeOrTaskLink = (linkRefType) =>
  linkRefType === KEY_EVENT_REF_TYPES.SCOPE || linkRefType === KEY_EVENT_REF_TYPES.TASK;

const emptyFormData = {
  title: "",
  type: KEY_EVENT_TYPES.TARGET_DATE,
  linkRefId: null,
  linkRefType: null,
  isShared: false,
  startDate: null,
  endDate: null,
  customFields: [],
  iconType: KEY_EVENT_ICON_TYPES.MILESTONE,
};

const KeyEventsPopover = ({
  open,
  onOpenChange,
  entityType,
  entityID,
  timelineData = [],
  directProjects = [],
  keyEvents = [],
  onCreateEvent,
  onUpdateEvent,
  onDeleteEvent,
  isLoading,
  children,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteEventId, setDeleteEventId] = useState(null);
  const [expandedPickerNodes, setExpandedPickerNodes] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});

  const [formData, setFormData] = useState(emptyFormData);
  const [errors, setErrors] = useState({});

  // Normalizes this entityType's timelineData shape into one tree the picker and grouped list both
  // walk generically - see buildKeyEventTree.js for the exact depth rules per level.
  const tree = useMemo(
    () => buildKeyEventTree(entityType, timelineData, directProjects),
    [entityType, timelineData, directProjects]
  );

  const handleBackToList = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData(emptyFormData);
    setErrors({});
    setShowAdvanced(false);
    setExpandedPickerNodes({});
  };

  const handleCreateClick = () => {
    setEditingEvent(null);
    setFormData(emptyFormData);
    setShowForm(true);
  };

  const handleQuickAdd = (linkRefId, linkRefType) => {
    setEditingEvent(null);
    setFormData({ ...emptyFormData, linkRefId, linkRefType, isShared: isScopeOrTaskLink(linkRefType) });

    // Expand every ancestor group of the target so it's visible (and reachable) in the picker tree.
    const ancestorIds = findAncestorIds(tree, linkRefId);
    if (ancestorIds.length > 0) {
      setExpandedPickerNodes(ancestorIds.reduce((acc, id) => ({ ...acc, [id]: true }), {}));
    }

    setShowForm(true);
  };

  // An event is only editable/deletable from the level that owns it (RefId) - one shared down
  // (linked in from an ancestor) can be viewed here but not modified, matching the backend's
  // NotOwner check.
  const isEventOwned = (event) => event.refId === entityID;

  const handleEditClick = (event) => {
    if (!isEventOwned(event)) return;
    setEditingEvent(event);
    setFormData({
      title: event.title,
      type: event.type,
      linkRefId: event.linkRefId,
      linkRefType: event.linkRefType,
      isShared: event.isShared,
      startDate: event.startDate,
      endDate: event.endDate,
      customFields: event.customFields || [],
      iconType: event.iconType ?? KEY_EVENT_ICON_TYPES.MILESTONE,
    });
    if (event.customFields && event.customFields.length > 0) {
      setShowAdvanced(true);
    }
    setShowForm(true);
  };

  const togglePickerNode = (id) => {
    setExpandedPickerNodes((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAddCustomField = () => {
    setFormData((prev) => ({
      ...prev,
      customFields: [...prev.customFields, { name: "", value: "" }],
    }));
  };

  const handleRemoveCustomField = (index) => {
    setFormData((prev) => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index),
    }));
  };

  const handleCustomFieldChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      customFields: prev.customFields.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.endDate) newErrors.endDate = "Date is required";
    if (formData.type === KEY_EVENT_TYPES.DATE_RANGE && !formData.startDate) {
      newErrors.startDate = "Start date is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // linkRefId/linkRefType/isShared carry forward unchanged on edit too (the picker is create-only
    // below, so formData already holds the original event's link - never mutated during an edit).
    const payload = {
      title: formData.title,
      iconType: formData.iconType,
      type: formData.type,
      startDate: formData.type === KEY_EVENT_TYPES.DATE_RANGE ? formData.startDate : null,
      endDate: formData.endDate,
      customFields: formData.customFields.filter(
        (field) => field.name.trim() && field.value.trim()
      ),
      linkRefId: formData.linkRefId || null,
      linkRefType: formData.linkRefId ? formData.linkRefType : null,
      isShared: formData.linkRefId ? formData.isShared : false,
    };

    if (editingEvent) {
      await onUpdateEvent(editingEvent.id, payload);
    } else {
      await onCreateEvent(payload);
    }
    handleBackToList();
  };

  const handleDeleteClick = (event) => {
    if (!isEventOwned(event)) return;
    setDeleteEventId(event.id);
  };

  const handleConfirmDelete = async () => {
    await onDeleteEvent(deleteEventId);
    setDeleteEventId(null);
  };

  const getRefName = (linkRefId) => {
    if (!linkRefId) return "";
    return findNodeInTree(tree, linkRefId)?.name || "";
  };

  // Filter events based on search query
  const filteredEvents = keyEvents.filter((event) =>
    event.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Bucket filtered events by their linkRefId (node id they're attached to). The tree only has
  // nodes for this level's descendants, not for this level itself - so an event with no link at
  // all, OR one linked straight back to the current entity (e.g. shared down from an ancestor that
  // targeted this level directly, not one of its children), both belong in the "Global" bucket.
  const { selfEvents, buckets } = useMemo(() => {
    const buckets = new Map();
    const selfEvents = [];

    filteredEvents.forEach((event) => {
      if (!event.linkRefId || event.linkRefId === entityID) {
        selfEvents.push(event);
        return;
      }
      if (!buckets.has(event.linkRefId)) buckets.set(event.linkRefId, []);
      buckets.get(event.linkRefId).push(event);
    });

    return { selfEvents, buckets };
  }, [filteredEvents, entityID]);

  // Recursively counts a node's own events + every descendant's, for the header badge.
  const countEvents = (node) => {
    let count = (buckets.get(node.id) || []).length;
    (node.children || []).forEach((child) => {
      count += countEvents(child);
    });
    return count;
  };

  // Initialize all groups (including the self/global bucket) as expanded once per open tree.
  useEffect(() => {
    if (tree.groups.length > 0 && Object.keys(expandedGroups).length === 0) {
      const initial = { self: true };
      const walk = (nodes) => {
        nodes.forEach((node) => {
          initial[node.id] = true;
          walk(node.children || []);
        });
      };
      walk(tree.groups);
      setExpandedGroups(initial);
    }
  }, [tree]);

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const renderEventCard = (event) => {
    const owned = isEventOwned(event);

    return (
    <div
      key={event.id}
      className={styles.eventCard}
      style={!owned ? { cursor: "default" } : undefined}
      onClick={owned ? () => handleEditClick(event) : undefined}
    >
      <div className={styles.eventContent}>
        <div className={styles.eventHeader}>
          <div className={styles.eventInfo}>
            <div className="d-flex align-items-center gap-2">
              <h4 className={styles.eventTitle}>{event.title}</h4>
              {!owned && (
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "#342C82",
                    backgroundColor: "#ECE7FD",
                    borderRadius: "10px",
                    padding: "1px 8px",
                    fontFamily: "Figtree, sans-serif",
                    flexShrink: 0,
                  }}
                  title="Shared from a higher level - only editable there"
                >
                  Shared
                </span>
              )}
            </div>
            <p className={styles.eventDate}>
              {event.type === KEY_EVENT_TYPES.TARGET_DATE
                ? event.endDate
                : `${event.startDate} - ${event.endDate}`}
            </p>
          </div>
          {owned && (
            <div className={styles.eventActions}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(event);
                }}
                className={styles.deleteButton}
                title="Delete"
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {event.customFields && event.customFields.length > 0 && (
        <div className={styles.customFieldsDisplay}>
          <div className={styles.customFieldsContent}>
            {event.customFields.map((field, index) => (
              <div key={index} className={styles.customFieldItem}>
                <span className={styles.customFieldName}>{field.name}:</span>
                <span className={styles.customFieldValue}>{field.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    );
  };

  // Recursively renders one grouped-list node: header (expand toggle, name, quick-add, count),
  // its own events, then its children groups at the next depth.
  const renderGroupNode = (node, depth) => {
    const isExpanded = expandedGroups[node.id];
    const ownEvents = buckets.get(node.id) || [];
    const totalCount = countEvents(node);
    const hasChildren = (node.children || []).length > 0;

    return (
      <div key={node.id} className={depth === 0 ? styles.programGroup : styles.projectGroup}>
        <div className={styles.groupHeader}>
          <div className={styles.groupHeaderLeft}>
            <div
              onClick={() => toggleGroup(node.id)}
              style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
            >
              <button className={styles.expandIcon}>
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              <span className={styles.groupTitle}>{node.name}</span>
            </div>
            <button
              className={styles.quickAddButton}
              onClick={(e) => {
                e.stopPropagation();
                handleQuickAdd(node.id, node.refType);
              }}
              title="Add event"
            >
              <Plus size={14} />
            </button>
          </div>
          <div className={styles.groupHeaderRight}>
            <span className={styles.eventCount}>{totalCount}</span>
          </div>
        </div>
        {isExpanded && (
          <>
            {ownEvents.length > 0 && (
              <div className={styles.groupEvents}>{ownEvents.map(renderEventCard)}</div>
            )}
            {ownEvents.length === 0 && !hasChildren && (
              <div className={styles.emptyGroupState}>
                <p className={styles.emptyGroupText}>No events yet</p>
              </div>
            )}
            {(node.children || []).map((child) => renderGroupNode(child, depth + 1))}
          </>
        )}
      </div>
    );
  };

  // Recursively renders one "attach to" picker node in the create form.
  const renderPickerNode = (node, depth) => {
    const isSelected = formData.linkRefId === node.id;
    const hasChildren = (node.children || []).length > 0;

    return (
      <div key={node.id} className={styles.programItem}>
        <div
          className={`${depth === 0 ? styles.programRow : styles.projectRow} ${isSelected ? styles.selected : ""}`}
          onClick={() => setFormData((prev) => ({
            ...prev,
            linkRefId: node.id,
            linkRefType: node.refType,
            // Scope/Task have no dedicated timeline page of their own yet, so "also show on its own
            // timeline" is moot - always shared, and the toggle itself is hidden for these types.
            isShared: isScopeOrTaskLink(node.refType) ? true : prev.isShared,
          }))}
        >
          {hasChildren && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                togglePickerNode(node.id);
              }}
              className={styles.expandButton}
            >
              {expandedPickerNodes[node.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}
          <span className={depth === 0 ? styles.programName : styles.projectName}>{node.name}</span>
          {isSelected && <div className={styles.selectionIndicator} />}
        </div>
        {hasChildren && expandedPickerNodes[node.id] && (
          <div className={styles.projectsList}>
            {node.children.map((child) => renderPickerNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(e, data) => {
          onOpenChange(data.open);
          if (!data.open) {
            handleBackToList();
            setSearchQuery("");
          }
        }}
        positioning="below-start"
        trapFocus
      >
        <PopoverTrigger disableButtonEnhancement>
          {children}
        </PopoverTrigger>

        <PopoverSurface className={styles.popoverSurface}>
          <div className={styles.popoverContent}>
            {/* Header */}
            <div className={styles.header}>
              <h3 className={styles.title}>
                {showForm ? (editingEvent ? "Edit Event" : "New Event") : "Key Events"}
              </h3>
              <button
                onClick={showForm ? handleBackToList : () => onOpenChange(false)}
                className={styles.closeButton}
              >
                <X size={16} />
              </button>
            </div>

            {/* Content */}
            <div className={styles.content}>
              {showForm ? (
                <div className={styles.formContainer}>
                  {/* Title */}
                  <Input
                    label="Title"
                    placeholder="Enter event title"
                    value={formData.title}
                    handleChange={(value) => setFormData(prev => ({ ...prev, title: value }))}
                    error={errors.title}
                    isRequired
                  />

                  {/* Attach to (create-only - the link target is immutable once created) */}
                  {!editingEvent && (
                    <div>
                      <label className="Input_label text-sm">
                        Attach to <span className="text-red-500">*</span>
                      </label>
                      {errors.linkRefId && (
                        <p className="text-xs text-red-500 mt-1">{errors.linkRefId}</p>
                      )}
                      <div className={styles.programProjectList}>
                        {/* Self / current-level option */}
                        <div className={styles.programItem}>
                          <div
                            className={`${styles.programRow} ${!formData.linkRefId ? styles.selected : ""}`}
                            onClick={() => setFormData(prev => ({ ...prev, linkRefId: null, linkRefType: null }))}
                          >
                            <span className={styles.programName}>{tree.selfLabel}</span>
                            {!formData.linkRefId && (
                              <div className={styles.selectionIndicator} />
                            )}
                          </div>
                        </div>

                        {tree.groups.map((node) => renderPickerNode(node, 0))}
                      </div>
                    </div>
                  )}

                  {/* Icon Type */}
                  <div>
                    <label className="Input_label text-sm">
                      Icon Type
                    </label>
                    <div className={styles.iconTypeButtons}>
                      {Object.entries(KEY_EVENT_ICONS).map(([type, { icon: Icon, label, color }]) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, iconType: parseInt(type) }))}
                          className={`${styles.iconTypeButton} ${formData.iconType === parseInt(type) ? styles.active : ""}`}
                        >
                          <Icon size={16} style={{ color: formData.iconType === parseInt(type) ? color : '#6b7280' }} />
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Event Type - available regardless of whether this event is attached to the
                      Global entity or a specific child. */}
                  <div>
                    <label className="Input_label text-sm">
                      Event Type
                    </label>
                    <div className={styles.eventTypeButtons}>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, type: KEY_EVENT_TYPES.TARGET_DATE }))}
                        className={`${styles.eventTypeButton} ${formData.type === KEY_EVENT_TYPES.TARGET_DATE ? styles.active : ""}`}
                      >
                        Target Date
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, type: KEY_EVENT_TYPES.DATE_RANGE }))}
                        className={`${styles.eventTypeButton} ${formData.type === KEY_EVENT_TYPES.DATE_RANGE ? styles.active : ""}`}
                      >
                        Date Range
                      </button>
                    </div>
                  </div>

                  {/* IsShared - only meaningful when linked to a descendant that has its own
                      dedicated Roadmap/Timeline page. Scope/Task don't have one yet, so the toggle
                      is hidden for those (always shared, forced by the backend). */}
                  {formData.linkRefId && !isScopeOrTaskLink(formData.linkRefType) && (
                    <label className="d-flex align-items-center gap-2" style={{ fontSize: "13px", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={!!formData.isShared}
                        onChange={(e) => setFormData(prev => ({ ...prev, isShared: e.target.checked }))}
                      />
                      <span>Also show on {getRefName(formData.linkRefId) || "linked entity"}'s own timeline</span>
                    </label>
                  )}


                  {/* Dates */}
                  <div className={styles.datesContainer}>
                    {formData.type === KEY_EVENT_TYPES.DATE_RANGE && (
                      <div className={styles.dateField}>
                        <Input
                          label="Start Date"
                          icon={<CalIcon />}
                          type="date"
                          value={formData.startDate}
                          maxDate={formData.endDate}
                          handleChange={(value) => setFormData(prev => ({ ...prev, startDate: value }))}
                          error={errors.startDate}
                          isRequired
                        />
                      </div>
                    )}
                    <div className={styles.dateField}>
                      <Input
                        label={formData.type === KEY_EVENT_TYPES.TARGET_DATE ? "Target Date" : "End Date"}
                        icon={<CalIcon />}
                        type="date"
                        value={formData.endDate}
                        minDate={formData.startDate}
                        handleChange={(value) => setFormData(prev => ({ ...prev, endDate: value }))}
                        error={errors.endDate}
                        isRequired
                      />
                    </div>
                  </div>

                  {/* Custom Fields Section */}
                  <div className={styles.customFieldsSection}>
                    <div className={styles.customFieldsHeader}>
                      <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className={styles.customFieldsToggle}
                      >
                        {showAdvanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span>Custom Fields</span>
                      </button>
                      {showAdvanced && (
                        <button
                          type="button"
                          onClick={handleAddCustomField}
                          className={styles.addFieldButton}
                          title="Add Field"
                        >
                          <Plus size={14} className="text-[#342C82]" />
                        </button>
                      )}
                    </div>

                    {showAdvanced && (
                      <div className={styles.customFieldsList}>
                        {formData.customFields.map((field, index) => (
                          <div key={index} className={styles.customFieldRow}>
                            <Input
                              placeholder="Name"
                              value={field.name}
                              handleChange={(value) => handleCustomFieldChange(index, "name", value)}
                            />
                            <Input
                              placeholder="Value"
                              value={field.value}
                              handleChange={(value) => handleCustomFieldChange(index, "value", value)}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveCustomField(index)}
                              className={styles.removeFieldButton}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                        {formData.customFields.length === 0 && (
                          <p className={styles.emptyFieldsMessage}>No custom fields added</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className={styles.formActions}>
                    <button
                      type="button"
                      onClick={handleBackToList}
                      disabled={isLoading}
                      className={styles.cancelButton}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={isLoading}
                      className={styles.saveButton}
                    >
                      {isLoading ? "Saving..." : editingEvent ? "Update" : "Create"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Search Bar */}
                  {keyEvents.length > 0 && (
                    <div className={styles.searchContainer}>
                      <div className={styles.searchWrapper}>
                        <div className={styles.searchInputWrapper}>
                          <Search size={14} className={styles.searchIcon} />
                          <input
                            type="text"
                            placeholder="Search events..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={styles.searchInput}
                          />
                        </div>
                        <button onClick={handleCreateClick} className={styles.addButton}>
                          <Plus size={14} />
                          <span>Add</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Events List */}
                  {keyEvents.length === 0 ? (
                    <div className={styles.emptyState}>
                      <CalendarIcon size={48} className={styles.emptyIcon} />
                      <p className={styles.emptyTitle}>No key events yet</p>
                      <p className={styles.emptySubtitle}>Click "Add" to create your first key event</p>
                      <button onClick={handleCreateClick} className={styles.addButton}>
                        <Plus size={14} />
                        <span>Add</span>
                      </button>
                    </div>
                  ) : filteredEvents.length === 0 ? (
                    <div className={styles.emptyState}>
                      <Search size={48} className={styles.emptyIcon} />
                      <p className={styles.emptyTitle}>No events found</p>
                      <p className={styles.emptySubtitle}>Try a different search term</p>
                    </div>
                  ) : (
                    <div className={styles.eventsList}>
                      {/* Self / current-level events */}
                      <div className={styles.groupContainer}>
                        <div className={styles.programGroup}>
                          <div className={styles.groupHeader}>
                            <div className={styles.groupHeaderLeft}>
                              <div onClick={() => toggleGroup('self')} style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                <button className={styles.expandIcon}>
                                  {expandedGroups['self'] ? (
                                    <ChevronDown size={16} />
                                  ) : (
                                    <ChevronRight size={16} />
                                  )}
                                </button>
                                <span className={styles.groupTitle}>{tree.selfLabel}</span>
                              </div>
                              <button
                                className={styles.quickAddButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQuickAdd(null, null);
                                }}
                                title="Add event"
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                            <div className={styles.groupHeaderRight}>
                              <span className={styles.eventCount}>{selfEvents.length}</span>
                            </div>
                          </div>
                          {expandedGroups['self'] && (
                            <>
                              {selfEvents.length > 0 ? (
                                <div className={styles.groupEvents}>{selfEvents.map(renderEventCard)}</div>
                              ) : (
                                <div className={styles.emptyGroupState}>
                                  <p className={styles.emptyGroupText}>No {tree.selfLabel.toLowerCase()} events yet</p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Hierarchy groups */}
                      {tree.groups.map((node) => renderGroupNode(node, 0))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </PopoverSurface>
      </Popover>

      {/* Delete Confirmation Modal */}
      {deleteEventId && (
        <Modal
          title="Delete Key Event"
          body={
            <p className="text-sm-400 subHeading-color" style={{ fontFamily: "Figtree, sans-serif" }}>
              Are you sure you want to delete this key event? This action cannot be undone.
            </p>
          }
          handleCancel={() => setDeleteEventId(null)}
          handleSave={handleConfirmDelete}
          saveBtnLabel="Delete"
          cancelBtnLabel="Cancel"
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default KeyEventsPopover;
